import { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Building2, FileText, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Customer {
  id: string;
  customer_number: string;
  name: string;
  mc_number?: string;
  dot_number?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_phone_ext?: string;
  billing_email?: string;
  billing_phone?: string;
  billing_phone_ext?: string;
  billing_street?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  transfer_documents?: boolean;
  email_notified?: boolean;
  transfer_to_factoring?: boolean;
  factoring_company?: string;
  notes?: string;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

interface ViewCustomerModalProps {
  customerId: string;
  onClose: () => void;
  onEdit: () => void;
}

export function ViewCustomerModal({ customerId, onClose, onEdit }: ViewCustomerModalProps) {
  const toast = useToast();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  async function loadCustomer() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (error) throw error;
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full p-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  const InfoRow = ({ label, value, icon: Icon }: { label: string; value?: string; icon?: any }) => {
    if (!value) return null;
    
    return (
      <div className="flex items-start gap-3 py-2">
        {Icon && <Icon className="w-5 h-5 text-slate-400 mt-0.5" />}
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-base text-slate-900">{value}</p>
        </div>
      </div>
    );
  };

  const BooleanBadge = ({ value, label }: { value?: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      {value ? (
        <CheckCircle className="w-5 h-5 text-green-600" />
      ) : (
        <XCircle className="w-5 h-5 text-slate-400" />
      )}
      <span className={`text-sm ${value ? 'text-slate-900' : 'text-slate-500'}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">{customer.name}</h2>
                <p className="text-sm text-blue-100 mt-1">
                  Customer #{customer.customer_number}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status Badge */}
          {customer.is_locked && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-semibold">🔒 This customer is locked</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="MC/MX/FF Number" value={customer.mc_number} icon={FileText} />
              <InfoRow label="DOT Number" value={customer.dot_number} icon={FileText} />
            </div>
          </div>

          {/* Contact Information */}
          {(customer.contact_name || customer.contact_email || customer.contact_phone) && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Primary Contact
              </h3>
              <div className="space-y-3">
                <InfoRow label="Contact Name" value={customer.contact_name} icon={Building2} />
                <InfoRow label="Email" value={customer.contact_email} icon={Mail} />
                <InfoRow 
                  label="Phone" 
                  value={customer.contact_phone ? 
                    `${customer.contact_phone}${customer.contact_phone_ext ? ` ext. ${customer.contact_phone_ext}` : ''}` 
                    : undefined
                  } 
                  icon={Phone} 
                />
              </div>
            </div>
          )}

          {/* Billing Contact */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Billing Contact
            </h3>
            <div className="space-y-3">
              <InfoRow label="Email" value={customer.billing_email} icon={Mail} />
              <InfoRow 
                label="Phone" 
                value={customer.billing_phone ? 
                  `${customer.billing_phone}${customer.billing_phone_ext ? ` ext. ${customer.billing_phone_ext}` : ''}` 
                  : undefined
                } 
                icon={Phone} 
              />
            </div>

            {/* Billing Address */}
            {(customer.billing_street || customer.billing_city || customer.billing_state || customer.billing_zip) && (
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-md font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  Billing Address
                </h4>
                <div className="ml-7 text-slate-900">
                  {customer.billing_street && <p>{customer.billing_street}</p>}
                  {(customer.billing_city || customer.billing_state || customer.billing_zip) && (
                    <p>
                      {customer.billing_city && `${customer.billing_city}, `}
                      {customer.billing_state && `${customer.billing_state} `}
                      {customer.billing_zip}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Load Billing Preferences */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Load Billing Preferences
            </h3>
            <div className="space-y-3">
              <BooleanBadge value={customer.transfer_documents} label="Transfer Documents" />
              <BooleanBadge value={customer.email_notified} label="Send documents by email" />
              <BooleanBadge value={customer.transfer_to_factoring} label="Use factoring company" />
              
              {customer.transfer_to_factoring && customer.factoring_company && (
                <div className="ml-7 mt-2">
                  <InfoRow 
                    label="Factoring Company" 
                    value={customer.factoring_company} 
                    icon={Building2} 
                  />
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                Notes
              </h3>
              <p className="text-slate-700 whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-slate-50 rounded-lg p-6 space-y-2">
            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Record Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Created</p>
                <p className="text-slate-900 font-medium">
                  {new Date(customer.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Last Updated</p>
                <p className="text-slate-900 font-medium">
                  {new Date(customer.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Edit Customer
          </button>
        </div>
      </div>
    </div>
  );
}
